import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// In-memory auth + workspace state (no localStorage)
let accessToken: string | null = null;
let activeWorkspaceId: string | null = null;

type LogoutHandler = () => void;
let logoutHandler: LogoutHandler | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

export const setActiveWorkspaceId = (workspaceId: string | null) => {
  activeWorkspaceId = workspaceId;
};

export const getActiveWorkspaceId = () => activeWorkspaceId;

export const setLogoutHandler = (handler: LogoutHandler | null) => {
  logoutHandler = handler;
};

/**
 * Fired on `window` whenever the cached trial status may be out of date — a
 * chat answered with one of the trial guard's codes below, or a test message
 * that went through and used one up. useTrialStatus listens for it.
 */
export const TRIAL_STATUS_STALE_EVENT = 'vina:trial-status-stale';

/** The codes backend TrialLimitsService.assertCanChat answers a chat with. */
const TRIAL_ERROR_CODES = ['TRIAL_LIMIT_REACHED', 'TRIAL_EXPIRED'];

export const markTrialStatusStale = () => {
  window.dispatchEvent(new Event(TRIAL_STATUS_STALE_EVENT));
};

const baseURL =
  process.env.REACT_APP_API_URL ||
  '/api';

const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true, // needed for httpOnly refresh cookie
});

// Separate client for refresh to avoid interceptor recursion
const refreshClient: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
});

// Attach auth + workspace to every request
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  if (activeWorkspaceId) {
    config.headers['x-workspace-id'] = activeWorkspaceId;
  }

  return config;
});

interface RefreshResponse {
  accessToken: string;
}

// Response interceptor: try refresh on 401 once
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest?._retry
    ) {
      originalRequest._retry = true;
      try {
        // Refresh uses httpOnly cookie (withCredentials: true)
        const refreshResponse = await refreshClient.post<RefreshResponse>('/auth/refresh');
        const newToken = refreshResponse.data.accessToken;
        setAccessToken(newToken);

        // Re-run original request with new token
        if (!originalRequest.headers) {
          originalRequest.headers = {};
        }
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed → logout user globally
        setAccessToken(null);
        if (logoutHandler) {
          logoutHandler();
        }
      }
    }

    // The trial guard just refused a chat: the banner should say so now rather
    // than on its next poll. The error still bubbles up to the caller.
    const code = (error.response?.data as { code?: string } | undefined)?.code;
    if (error.response?.status === 403 && code && TRIAL_ERROR_CODES.includes(code)) {
      markTrialStatusStale();
    }

    // Any other error just bubbles up
    return Promise.reject(error);
  },
);

export default api;

